/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useState, useEffect, useRef } from "react";

import styled from "styled-components";

interface AudioPlayerProps {
  src: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const togglePlay = () => {
    if (audioRef.current) {
      if (playing) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  useEffect(() => {
    const handleTimeUpdate = () => {
      if (audioRef.current) {
        const percentage = (audioRef.current.currentTime / audioRef.current.duration) * 100;
        setProgress(percentage);
      }
    };

    if (audioRef.current) {
      audioRef.current.addEventListener("timeupdate", handleTimeUpdate);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener("timeupdate", handleTimeUpdate);
      }
    };
  }, []);

  return (
    <AudioPlayerContainer>
      <PlayButton onClick={togglePlay}>
        {playing ? (
          <svg width="20" height="25" viewBox="0 0 20 31" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Pause SVG */}
            <rect x="0" y="0" width="7" height="31" fill="black" />
            <rect x="13" y="0" width="7" height="31" fill="black" />
          </svg>
        ) : (
          <svg width="20" height="25" viewBox="0 0 20 31" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Play SVG */}
            <path d="M0 0 L20 15.5 L0 31 L0 0" fill="black" />
          </svg>
        )}
      </PlayButton>

      <ProgressBarContainer>
        <ProgressBar style={{ width: `${progress}%` }} />
      </ProgressBarContainer>

      <audio ref={audioRef} src={src} onEnded={() => setPlaying(false)}></audio>
    </AudioPlayerContainer>
  );
};

export default AudioPlayer;

const AudioPlayerContainer = styled.div`
  display: flex;
  align-items: center;
`;

const PlayButton = styled.button`
  width: 40px;
  height: 40px;
  background-color: #fafafa;
  border: 2px solid #fafafa;
  cursor: pointer;
  outline: none;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ProgressBarContainer = styled.div`
  flex-grow: 1;
  height: 10px;
  width: 140px;
  background-color: #f2f2f2;
  margin: 0 10px;
  position: relative;
`;

const ProgressBar = styled.div`
  height: 100%;
  background-color: #000;
  transition: width 0.3s ease;
`;
